/**
 * Tenants Management Page
 *
 * Enterprise features for billion-scale:
 * - Cursor-based pagination (scales to millions of tenants)
 * - Debounced search with request cancellation
 * - Optimistic updates for status changes
 * - Request deduplication
 * - Infinite scroll with virtualization-ready structure
 * - Real-time tenant stats updates
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { CreateTenantModal, TenantDrawer } from '../../components/modals';
import { tenantsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useQuery, useMutation, useDebouncedValue, useIntersectionObserver } from '../../hooks/useDataFetch';
import { requestManager } from '../../lib/RequestManager';
import './Tenants.css';

// Constants
const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

const Tenants = () => {
  const { showToast } = useToast();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [cursor, setCursor] = useState(null);
  const [allTenants, setAllTenants] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Debounced search
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  // Build query params
  const queryParams = useMemo(() => ({
    limit: PAGE_SIZE,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { status: statusFilter }),
    ...(planFilter && { plan: planFilter }),
    ...(cursor && { cursor }),
  }), [debouncedSearch, statusFilter, planFilter, cursor]);

  // Cache key for request deduplication
  const cacheKey = useMemo(() =>
    `tenants:${debouncedSearch}:${statusFilter}:${planFilter}:${cursor}`,
    [debouncedSearch, statusFilter, planFilter, cursor]
  );

  // Fetch tenants
  const {
    error,
    isLoading,
    isFetching,
    refetch,
  } = useQuery(
    () => tenantsApi.list(queryParams),
    {
      key: cacheKey,
      cacheTTL: 30000,
      enabled: true,
      onSuccess: (response) => {
        const newTenants = response.companies || response.data || response.items || [];
        const nextCursor = response.nextCursor || response.pagination?.nextCursor;

        if (cursor) {
          // Append for pagination
          setAllTenants(prev => [...prev, ...newTenants]);
        } else {
          // Replace for new search/filter
          setAllTenants(newTenants);
        }

        setHasMore(!!nextCursor && newTenants.length === PAGE_SIZE);
      },
      onError: (err) => {
        showToast(err.message || 'Failed to load tenants', 'error');
      },
    }
  );

  // Reset when filters change
  useEffect(() => {
    setCursor(null);
    setAllTenants([]);
    setHasMore(true);
    requestManager.invalidateCache(`tenants:${debouncedSearch}:${statusFilter}:${planFilter}`);
  }, [debouncedSearch, statusFilter, planFilter]);

  // Infinite scroll observer
  const [loadMoreRef, isNearBottom] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '200px',
  });

  // Load more when scrolling
  useEffect(() => {
    if (isNearBottom && hasMore && !isLoading && !isFetching && allTenants.length > 0) {
      const lastTenant = allTenants[allTenants.length - 1];
      if (lastTenant?.id) {
        setCursor(lastTenant.id);
      }
    }
  }, [isNearBottom, hasMore, isLoading, isFetching, allTenants]);

  // Suspend mutation with optimistic update
  const suspendMutation = useMutation(
    (tenantId) => tenantsApi.suspend(tenantId),
    {
      onMutate: (tenantId) => {
        // Optimistic update
        const previousTenants = [...allTenants];
        setAllTenants(prev =>
          prev.map(t => t.id === tenantId ? { ...t, status: 'suspended' } : t)
        );
        return () => setAllTenants(previousTenants);
      },
      onSuccess: () => {
        showToast('Tenant suspended', 'success');
      },
      onError: (err) => {
        showToast(err.message || 'Failed to suspend tenant', 'error');
      },
      invalidateKeys: ['tenants'],
    }
  );

  // Reactivate mutation with optimistic update
  const reactivateMutation = useMutation(
    (tenantId) => tenantsApi.reactivate(tenantId),
    {
      onMutate: (tenantId) => {
        // Optimistic update
        const previousTenants = [...allTenants];
        setAllTenants(prev =>
          prev.map(t => t.id === tenantId ? { ...t, status: 'active' } : t)
        );
        return () => setAllTenants(previousTenants);
      },
      onSuccess: () => {
        showToast('Tenant reactivated', 'success');
      },
      onError: (err) => {
        showToast(err.message || 'Failed to reactivate tenant', 'error');
      },
      invalidateKeys: ['tenants'],
    }
  );

  // Handlers
  const handleRefresh = useCallback(() => {
    setCursor(null);
    setAllTenants([]);
    requestManager.invalidateCache('tenants');
    refetch();
  }, [refetch]);

  const handleCreateSuccess = useCallback((newTenant) => {
    setAllTenants(prev => [newTenant, ...prev]);
    setIsCreateModalOpen(false);
    showToast('Tenant created successfully', 'success');
  }, [showToast]);

  const handleSuspend = useCallback((tenantId) => {
    if (window.confirm('Are you sure you want to suspend this tenant?')) {
      suspendMutation.mutate(tenantId);
    }
  }, [suspendMutation]);

  const handleReactivate = useCallback((tenantId) => {
    reactivateMutation.mutate(tenantId);
  }, [reactivateMutation]);

  const handleViewTenant = useCallback((tenant) => {
    setSelectedTenant(tenant);
    setIsDrawerOpen(true);
  }, []);

  const handleTenantUpdate = useCallback((updatedTenant) => {
    setAllTenants(prev =>
      prev.map(t => t.id === updatedTenant.id ? updatedTenant : t)
    );
  }, []);

  // Helpers
  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const getQuotaPercent = (tenant) => {
    if (tenant.apiUsage && tenant.apiLimit) {
      return Math.min(100, Math.round((tenant.apiUsage / tenant.apiLimit) * 100));
    }
    return tenant.quotaPercent || 0;
  };

  const getQuotaClass = (percent) => {
    if (percent >= 90) return 'quota-red';
    if (percent >= 70) return 'quota-yellow';
    return 'quota-green';
  };

  // Memoized stats
  const stats = useMemo(() => ({
    total: allTenants.length + (hasMore ? '+' : ''),
    active: allTenants.filter(t => t.status === 'active').length,
    suspended: allTenants.filter(t => t.status === 'suspended').length,
    onboarding: allTenants.filter(t => t.status === 'onboarding').length,
  }), [allTenants, hasMore]);

  return (
    <div className="tenants-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Tenants</h1>
          <p className="page-subtitle">
            Manage your tenant organizations
            {allTenants.length > 0 && (
              <span className="tenants-count"> ({stats.total} loaded)</span>
            )}
          </p>
        </div>
        <div className="page-actions">
          <Button
            variant="secondary"
            icon="refresh"
            onClick={handleRefresh}
            disabled={isLoading || isFetching}
          >
            {isFetching ? 'Syncing...' : 'Sync'}
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Tenant
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="tenants-stats">
        <div className="stat-pill">
          <span className="stat-dot active"></span>
          <span>{stats.active} Active</span>
        </div>
        <div className="stat-pill">
          <span className="stat-dot onboarding"></span>
          <span>{stats.onboarding} Onboarding</span>
        </div>
        <div className="stat-pill">
          <span className="stat-dot suspended"></span>
          <span>{stats.suspended} Suspended</span>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="tenants-error">
          <Icon name="alert-triangle" size="sm" />
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={handleRefresh}>Retry</Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="tenants-toolbar">
        <div className="search-box">
          <Icon name="search" size="sm" />
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search tenants"
          />
          {searchQuery && (
            <button
              className="search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <Icon name="x" size="xs" />
            </button>
          )}
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="onboarding">Onboarding</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            className="filter-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            aria-label="Filter by plan"
          >
            <option value="">All Plans</option>
            <option value="enterprise">Enterprise</option>
            <option value="pro">Pro</option>
            <option value="starter">Starter</option>
          </select>
        </div>
      </div>

      {/* Tenant Grid */}
      <div className="tenant-grid">
        {/* Loading skeleton */}
        {isLoading && allTenants.length === 0 && (
          [...Array(6)].map((_, i) => (
            <div key={`skeleton-${i}`} className="tenant-card skeleton">
              <div className="tenant-header">
                <div className="tenant-info">
                  <div className="skeleton-line" style={{ width: '60%', height: '20px' }}></div>
                  <div className="skeleton-line short" style={{ width: '40%', height: '14px' }}></div>
                </div>
                <div className="skeleton-badge"></div>
              </div>
              <div className="tenant-stats">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="tenant-stat">
                    <div className="skeleton-line" style={{ width: '50%', height: '24px', margin: '0 auto' }}></div>
                    <div className="skeleton-line short" style={{ width: '70%', height: '12px', margin: '8px auto 0' }}></div>
                  </div>
                ))}
              </div>
              <div className="quota-bar">
                <div className="skeleton-line" style={{ height: '8px' }}></div>
              </div>
              <div className="tenant-actions">
                <div className="skeleton-line" style={{ width: '100%', height: '32px' }}></div>
              </div>
            </div>
          ))
        )}

        {/* Tenant cards */}
        {allTenants.map(tenant => {
          const quotaPercent = getQuotaPercent(tenant);
          return (
            <div key={tenant.id} className="tenant-card" data-tenant-id={tenant.id}>
              <div className="tenant-header">
                <div className="tenant-info">
                  <h3 className="tenant-name">{tenant.name}</h3>
                  <div className="tenant-domain">{tenant.domain || tenant.subdomain || '-'}</div>
                </div>
                <span className={`tenant-status status-${tenant.status || 'active'}`}>
                  {tenant.status || 'active'}
                </span>
              </div>

              <div className="tenant-stats">
                <div className="tenant-stat">
                  <div className="tenant-stat-value">
                    {formatNumber(tenant.usersCount || tenant.users_count || tenant.users)}
                  </div>
                  <div className="tenant-stat-label">Users</div>
                </div>
                <div className="tenant-stat">
                  <div className="tenant-stat-value">
                    {formatCurrency(tenant.mrr || tenant.revenue)}
                  </div>
                  <div className="tenant-stat-label">MRR</div>
                </div>
                <div className="tenant-stat">
                  <div className="tenant-stat-value">{tenant.plan || tenant.planName || '-'}</div>
                  <div className="tenant-stat-label">Plan</div>
                </div>
              </div>

              <div className="quota-bar">
                <div className="quota-label">
                  <span>API Quota</span>
                  <span>{quotaPercent}%</span>
                </div>
                <div className="quota-progress">
                  <div
                    className={`quota-fill ${getQuotaClass(quotaPercent)}`}
                    style={{ width: `${quotaPercent}%` }}
                  ></div>
                </div>
              </div>

              <div className="tenant-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  icon="eye"
                  onClick={() => handleViewTenant(tenant)}
                >
                  View
                </Button>
                <Button variant="ghost" size="sm" icon="edit">Edit</Button>
                {tenant.status === 'suspended' ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="play"
                    onClick={() => handleReactivate(tenant.id)}
                    disabled={reactivateMutation.isLoading}
                  >
                    Reactivate
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="pause"
                    onClick={() => handleSuspend(tenant.id)}
                    disabled={suspendMutation.isLoading}
                  >
                    Suspend
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {!isLoading && allTenants.length === 0 && (
          <div className="tenants-empty">
            <Icon name="building" size="lg" />
            <h3>No tenants found</h3>
            <p>
              {searchQuery || statusFilter || planFilter
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first tenant'}
            </p>
            {!searchQuery && !statusFilter && !planFilter && (
              <Button variant="primary" icon="plus" onClick={() => setIsCreateModalOpen(true)}>
                Add Tenant
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Load more trigger for infinite scroll */}
      {hasMore && allTenants.length > 0 && (
        <div ref={loadMoreRef} className="load-more-trigger">
          {isFetching && (
            <div className="loading-more">
              <div className="spinner"></div>
              <span>Loading more tenants...</span>
            </div>
          )}
        </div>
      )}

      {/* End of list */}
      {!hasMore && allTenants.length > 0 && (
        <div className="end-of-list">
          All {allTenants.length} tenants loaded
        </div>
      )}

      {/* Create Tenant Modal */}
      <CreateTenantModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Tenant Drawer */}
      <TenantDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        tenant={selectedTenant}
        onTenantUpdate={handleTenantUpdate}
      />
    </div>
  );
};

export default Tenants;
